import React, { FC } from 'react';
import classNames from 'classnames';
import { Modal, Radio } from 'antd';
import { CommonLabelProps } from '../../interface';
import { Action, ClassificationLabelingState } from './interface';
import { LabelLayout, LabelLayoutHeader } from '../layout';
import { LoadableAntdButton } from '@/ui/loadable-antd-button';
import { noop } from '@@/utils';
import { CombineProvider, ItemSelect, SpuSelectableItem } from '../item-select';
import { SimilarProduct } from '@/interface/label';
import { useModal } from '@@/react-hooks';
import { SpuDisplay } from '../spu-display';
import { NoMoreLabeling } from '../no-more-labeling';
import { Gallery } from '@/ui/gallery';
import addImage from './add.png';
import commonStyles from '../../common.module.less';
import styles from './classify-labeler-core.module.less';
import { LabelType } from '@/enum';

const EMPTY_SPU_FOR_LABEL: SimilarProduct = {
  brand: '',
  category1: '',
  category2: '',
  color: '',
  composition: '',
  currency: '',
  description: '',
  gender: null,
  id: null,
  image: addImage,
  images: [],
  name: '新商品',
  price: 0,
  retailer: '',
};

/**
 * 核心标注组件
 */
export const ClassifyLabelerCore: FC<
  CommonLabelProps & {
    data: ClassificationLabelingState;
    dispatch(action: Action): void;
  }
> = ({ data, dispatch, ...filterProps }) => {
  const { results, products, index } = data;
  const [zoomModalState, openZoomModal, closeZoomModal] = useModal<SimilarProduct>(null);

  const contentNode = (() => {
    const currentItem = products[index];

    if (!currentItem) {
      return <NoMoreLabeling />;
    }

    const { classifySpuId } = results[index];

    return (
      <>
        <div className={commonStyles.left_of_2}>
          <div className={commonStyles.vertical_container}>
            <header className={commonStyles.header}>
              <h3 className={commonStyles.title}>当前商品</h3>
            </header>
            <SpuDisplay data={currentItem} />
          </div>
        </div>
        <div className={commonStyles.right_of_2}>
          <div className={commonStyles.vertical_container}>
            <header className={commonStyles.header}>
              <h3 className={commonStyles.title}>选择左侧商品应分入的商品品类，拖拽以合并相同的商品</h3>
            </header>
            <ItemSelect>
              <CombineProvider
                source="classify-drag"
                onCombineFinish={data =>
                  dispatch({
                    type: 'combine',
                    payload: data,
                  })
                }
              >
                {[EMPTY_SPU_FOR_LABEL, ...currentItem.similarProducts].map(data => {
                  const checked = data.id === classifySpuId;
                  return (
                    <SpuSelectableItem
                      key={data.id}
                      cols={3}
                      data={data}
                      checked={checked}
                      onClick={
                        checked
                          ? noop
                          : () =>
                            dispatch({
                              type: 'label',
                              payload: {
                                classifySpuId: data.id,
                              },
                            })
                      }
                      onZoom={() => openZoomModal(data)}
                    >
                      <Radio className={commonStyles.checker} checked={checked} />
                    </SpuSelectableItem>
                  );
                })}
              </CombineProvider>
            </ItemSelect>
          </div>
        </div>
      </>
    );
  })();

  return (
    <LabelLayout>
      <LabelLayoutHeader {...filterProps} type={LabelType.Classify} >
        <div className={styles.indexes}>
          {results.map(({ isValid }, i) => {
            const selected = i === index;
            const onClick = selected
              ? noop
              : () =>
                dispatch({
                  type: 'to',
                  payload: i,
                });
            return (
              <div
                className={classNames(styles.index, {
                  [styles['index--selected']]: selected,
                })}
                onClick={onClick}
                key={i}
              >
                {i + 1}
                {isValid ? <div className={styles.valid_badge} /> : null}
              </div>
            );
          })}
        </div>
        <LoadableAntdButton
          onClick={() =>
            dispatch({
              type: 'submit',
            })
          }
        >
          提交该批次
        </LoadableAntdButton>
      </LabelLayoutHeader>

      {contentNode}

      <Modal
        visible={zoomModalState.visible}
        onCancel={closeZoomModal}
        title={null}
        footer={null}
        width="50vw"
        mask={false}
        maskClosable={false}
        style={{ left: '50vw', margin: 0 }}
      >
        {zoomModalState.data && <Gallery data={zoomModalState.data.images} />}
      </Modal>
    </LabelLayout>
  );
};
